import { useTranslation } from 'react-i18next';
import { DocumentTextOutline, Notes, OutlineDanger } from '../../icons';
import { Button, Text } from '../../Ant';
import { Flex } from 'antd';
import { TransactionActionWrapper } from '../../../assets/styles/components/dashboard.styled';
import { TaskSections } from '../../../constants/tasks';
import { MailMetadata } from '../../../interface/mailMetadata';
import { IMatter } from '../../../interface/matter';
import { FeatureSelectionTab } from '../../../interface/featureSelection';

interface TransactionActionPanelProps {
  mailMetadata: MailMetadata | null;
  selectedMatter: IMatter | undefined;
  features: Record<string, boolean>;
  onAddTransaction: () => void;
  onAddTask: (type: string) => void;
  onLinkToMatter: () => void;
  onUnlinkToMatter: () => void;
  disableMatterLinking?: boolean;
}

const TransactionActionPanel = ({
  mailMetadata,
  selectedMatter,
  features,
  onAddTransaction,
  onAddTask,
  onLinkToMatter,
  onUnlinkToMatter,
  disableMatterLinking,
}: TransactionActionPanelProps) => {
  const { t } = useTranslation();

  const tasksEnabled = features[FeatureSelectionTab.tasks];
  const showTransaction = !mailMetadata?.transactionId;
  const showTask = tasksEnabled && !mailMetadata?.taskId;
  const showCriticalTask = tasksEnabled && !mailMetadata?.criticalTaskId;
  const actionCount = [showTransaction, showTask, showCriticalTask].filter(Boolean).length;
  // When a single action button is left, hide its icon, make it large and use the "add new" label.
  const isSingleAction = actionCount === 1;
  const showActionIcon = !isSingleAction;
  const matterLinkButton = mailMetadata?.matterId
    ? {
        testId: 'unlink-to-matter-button',
        label: t('transaction:unlinkToMatter'),
        height: undefined,
        onClick: onUnlinkToMatter,
        looksUnavailable: disableMatterLinking,
      }
    : {
        testId: 'link-to-matter-button',
        label: t('transaction:linkToMatter'),
        height: '30px',
        onClick: onLinkToMatter,
        looksUnavailable: !selectedMatter || disableMatterLinking,
      };

  return (
    <TransactionActionWrapper vertical gap={5}>
      <Text $fontsize="12px" $weight={500} $color="description" $lineheight="16px">
        {t('common:selectActionToAdd')}
        {!selectedMatter && (
          <Text $fontsize="11px" $weight={400}>
            {` - ${t('common:pickMatterToActivate')}`}
          </Text>
        )}
      </Text>
      <Flex gap={5} className="action-btn-group w-full">
        {showTransaction && (
          <Button
            type="primary"
            size={isSingleAction ? 'large' : undefined}
            onClick={onAddTransaction}
            className={`action-btn flex-col ${selectedMatter ? '' : 'looks-disabled'}`}
            data-testid="add-transaction-button"
            data-entry-focus={selectedMatter ? true : undefined}
          >
            {showActionIcon && <DocumentTextOutline />}
            <Text $fontsize="12px" $weight={500} $color="white" $lineheight="normal">
              {isSingleAction ? t('transaction:addNew') : t('transaction:transaction')}
            </Text>
          </Button>
        )}
        {showTask && (
          <Button
            type="primary"
            size={isSingleAction ? 'large' : undefined}
            data-testid="add-general-task-button"
            onClick={() => onAddTask(TaskSections.generalTasks)}
            className={`action-btn flex-col ${selectedMatter ? '' : 'looks-disabled'}`}
          >
            {showActionIcon && <Notes />}
            <Text $fontsize="12px" $weight={500} $color="white" $lineheight="normal">
              {isSingleAction ? t('task:addNew') : t('task:task')}
            </Text>
          </Button>
        )}
        {showCriticalTask && (
          <Button
            type="primary"
            size={isSingleAction ? 'large' : undefined}
            data-testid="add-critical-task-button"
            onClick={() => onAddTask(TaskSections.criticalTasks)}
            className={`action-btn flex-col ${selectedMatter ? '' : 'looks-disabled'}`}
          >
            {showActionIcon && <OutlineDanger />}
            <Text $fontsize="12px" $weight={500} $color="white" $lineheight="normal">
              {isSingleAction ? t('task:criticalTask.addNew') : t('task:criticalTask.criticalTask')}
            </Text>
          </Button>
        )}
      </Flex>
      {/* One button that switches action, so keyboard focus stays on it when the link flips. */}
      <Button
        block
        size="large"
        height={matterLinkButton.height}
        variant="outlined"
        color="primary"
        data-testid={matterLinkButton.testId}
        onClick={matterLinkButton.onClick}
        disabled={disableMatterLinking}
        className={`flex-shrink-0 ${matterLinkButton.looksUnavailable ? 'looks-disabled' : ''}`}
      >
        {matterLinkButton.label}
      </Button>
    </TransactionActionWrapper>
  );
};

export default TransactionActionPanel;
